import { RatingItem } from "../../../../../../../components/rating-selector/rating-selector";
import { GetVesselEmissionsRequest } from "../../../../models/mep-port-models";
import { MepService } from "../../../../../../../../services/mep/mep.service";
import { UserClaimsService } from "../../../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../../../constants/authentication/claims";
import { MepLookupService } from "../../../../../../../../services/mep/mep-lookup.service";
import { BaseMepFilterViewModel, MepLookupItem } from "../../../../../../../../models/mep/mep.models";
import { PortVesselsOdataGridViewModel } from "./mep-vessels-odata-grid";
import { MepLookupFiltersHandler } from "../../../../common-vm/mep-lookup-filters-handler";
import { computedFrom } from "aurelia-binding";
import { RightShipDialogServiceConstants } from "../../../../../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { CustomerDetailsModel } from "../../../../../../../../models/customers/customer.models";
import { Reflection } from "../../../../../../../../utilities/reflection";
import { useView } from "aurelia-framework";

@useView('views/pages/modules/mep/tabs/mep-vessels/partials/mep-vessels-filters/mep-vessels-filters.html')
export class PortVesselsFiltersViewModel extends BaseMepFilterViewModel {
    public isMobileFilterOpen: boolean;
    public isDownloading: boolean = false;
    public isDownloadingTemplate: boolean = false;
    public disableEmissionTypesColumnsReload: boolean = false;

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.emissionTypes')
    get emissionTypes(): MepLookupItem[] {
        return this.lookupFiltersHandler.emissionTypes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.pointsOfInterests')
    get pointsOfInterests(): MepLookupItem[] {
        return this.lookupFiltersHandler.pointsOfInterests;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.operationModes')
    get operationModes(): MepLookupItem[] {
        return this.lookupFiltersHandler.operationModes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.cargoTypes')
    get cargoTypes(): MepLookupItem[] {
        return this.lookupFiltersHandler.cargoTypes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.vesselTypeGroups')
    get vesselTypeGroups(): MepLookupItem[] {
        return this.lookupFiltersHandler.vesselTypeGroups;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.weightBuckets')
    get weightBuckets(): MepLookupItem[] {
        return this.lookupFiltersHandler.weightBuckets;
    }

    public portUnlocode: string;
    public portName: string;

    //Emission Type Filter
    selectedEmissionTypes: number[] = [];

    //Point of Interest Filter
    selectedPointsOfInterest: number[] = [];

    //Operation Mode Filter
    selectedOperationModes: number[] = [];

    //Vessel Type Filter
    selectedVesselTypeGroups: number[] = [];

    //Cargo Type Filter
    selectedCargoTypes: number[] = [];

    public showEmissionTypeFilter: boolean = false;

    public showPoiFilter: boolean = false;

    public showVesselTypeFilter: boolean = false;

    public showOperationModeFilter: boolean = false;

    public showCargoTypeFilter: boolean = false;

    public showSafetyRatingFilter: boolean = false;
    public selectedSafetyRatings: number[] = [];
    public mobileSafetyRatings: number[] = [];

    public showGhgRatingFilter: boolean = false;
    public selectedGhgRatingWeights: number[] = [];
    public mobileGhgRatingWeights: number[] = [];

    public showDwtFilter: boolean = false;
    public selectedDwtBucketIds: number[] = [];
    public mobileDwtFilter: number[] = [];

    public showAgeFilter: boolean = false;
    public ageFirstFilter: number;
    public ageSecondFilter: number;
    public mobileAgeFilter: { low: number, high: number };
    public ageDefaults: { low: number, high: number } = { low: 0, high: 100 };
    public ageIsApplied: boolean = false;

    public searchFilter: string = '';

    public portVesselsOdataGridViewModel: PortVesselsOdataGridViewModel;
    public emissionTypesHtml: MepLookupItem[];
    public params: any;
    public supportsCargoType: boolean;

    public canShowSafetyScoreFilter : boolean;    
    public  canShowGhgRatingFilter: boolean;     

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.isLookupDataRefreshing')
    get isLookupDataRefreshing(): boolean {
        return this.lookupFiltersHandler.isLookupDataRefreshing;
    }

    applyFiltersFunc: () => void;

    constructor(portVesselsOdataGridViewModel: PortVesselsOdataGridViewModel, private dialogClientService: DialogClientService,
        private mepService: MepService, private userClaimsService: UserClaimsService, mepLookupService: MepLookupService,
        private customerDetails: CustomerDetailsModel, params: any, supportsCargoType: boolean) {
        super();
        this.portVesselsOdataGridViewModel = portVesselsOdataGridViewModel;

        if (params && params.portId) {
            this.portId = params.portId;
        }

        if (params && params.imo) {
            this.searchFilter = params.imo;
        }

        this.applyFiltersFunc = () => this.applyFilters();
        this.lookupFiltersHandler = new MepLookupFiltersHandler(params ? params.portId : null, this.applyFiltersFunc, mepLookupService);
        this.lookupFiltersHandler.includeWeightBucketsLookup = true;

        //params would be pre set from other pages in the query string 
        if (params && params.year) {
            this.lookupFiltersHandler.selectedYear = params.year;
        }
        this.params = params;

        this.supportsCargoType = supportsCargoType;

        this.canShowSafetyScoreFilter = this.userClaimsService.hasPermission(Claims.ShowSafetyScore);
        this.canShowGhgRatingFilter = this.userClaimsService.hasPermission(Claims.ShowGHG);
    }

    applyFilters(forceRefresh: boolean = false) {
        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest(), forceRefresh);
    }

    initLookups(params: any = null) {
        params = params ? params : this.params;

        return this.lookupFiltersHandler.initLookups()
            .then(() => {
                this.lookupFiltersHandler.skipRefreshOfLookupsBecauseOfInit = false;
                // Copy emission types to a new object to display subscripts
                this.setEmissionTypesHtml();

                this.portVesselsOdataGridViewModel.allowedEmissionTypesColumns = this.emissionTypes.map(x => x.name.toLowerCase());
                this.setFiltersFromParams(params);
            });
    }

    setFiltersFromParams(params: any) {
        if (params.year)
            this.lookupFiltersHandler.selectedYear = params.year;

        if (params.emissionTypeIds && params.emissionTypeIds.length > 0) {
            this.selectedEmissionTypes = params.emissionTypeIds.map(x => Number(x));
            this.portVesselsOdataGridViewModel.allowedEmissionTypesColumns = this.emissionTypes.filter(x => this.selectedEmissionTypes.indexOf(x.id) > -1).map(x => x.name.toLowerCase());
            this.disableEmissionTypesColumnsReload = true;
        }

        if (params.pointOfInterestIds)
            this.selectedPointsOfInterest = params.pointOfInterestIds.map(x => Number(x));

        if (params.vesselTypeGroupIds)
            this.selectedVesselTypeGroups = params.vesselTypeGroupIds.map(x => Number(x));

        if (params.operationModeIds)
            this.selectedOperationModes = params.operationModeIds.map(x => Number(x));

        if (params.cargoTypeIds)
            this.selectedCargoTypes = params.cargoTypeIds.map(x => Number(x));

        if (params.portId) {
            this.portId = params.portId;
            this.supportsCargoType = params.supportsCargoType;
        }

        if (params.scopeIds) {
            this.selectedScopes = params.scopeIds.map(x => Number(x));
        }

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
    }
 
    @computedFrom(Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showEmissionTypeFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showPoiFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showVesselTypeFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showOperationModeFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showSafetyRatingFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showGhgRatingFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showDwtFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showAgeFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showCargoTypeFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showScopeFilter))
    get mobileFilterSelected() {
        return !this.showEmissionTypeFilter || !this.showPoiFilter || !this.showVesselTypeFilter || !this.showOperationModeFilter ||
            !this.showSafetyRatingFilter || !this.showGhgRatingFilter || !this.showDwtFilter || !this.showAgeFilter || this.showCargoTypeFilter || this.showScopeFilter;
    }
   
    @computedFrom(Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showEmissionTypeFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showPoiFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showVesselTypeFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showOperationModeFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showSafetyRatingFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showGhgRatingFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showDwtFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showAgeFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showCargoTypeFilter),
        Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.showScopeFilter))
    get isSubFilterOpen(): boolean {
        return this.showEmissionTypeFilter || this.showPoiFilter || this.showVesselTypeFilter || this.showOperationModeFilter ||
            this.showSafetyRatingFilter || this.showGhgRatingFilter || this.showDwtFilter || this.showAgeFilter || this.showCargoTypeFilter || this.showScopeFilter;
    }

    toggleMobileFilterDialog() {

        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.showEmissionTypeFilter = false;
        this.showPoiFilter = false;
        this.showVesselTypeFilter = false;
        this.showOperationModeFilter = false;
        this.showSafetyRatingFilter = false;
        this.showGhgRatingFilter = false;
        this.showDwtFilter = false;
        this.showAgeFilter = false;
        this.showCargoTypeFilter = false;
        this.showScopeFilter = false;
    }

    toggleEmissionTypeFilters() {
        this.showEmissionTypeFilter = !this.showEmissionTypeFilter;
    }

    togglePoiFilters() {
        this.showPoiFilter = !this.showPoiFilter;
    }

    toggleVesselTypeFilters() {
        this.showVesselTypeFilter = !this.showVesselTypeFilter;
    }

    toggleOperationModeFilters() {
        this.showOperationModeFilter = !this.showOperationModeFilter;
    }

    toggleCargoTypeFilters() {
        this.showCargoTypeFilter = !this.showCargoTypeFilter;
    }

    toggleSafetyRatingFilters() {
        this.showSafetyRatingFilter = !this.showSafetyRatingFilter;
    }

    toggleGhgRatingFilters() {
        this.showGhgRatingFilter = !this.showGhgRatingFilter;
    }

    toggleDwtFilters() {
        this.showDwtFilter = !this.showDwtFilter;
    }

    toggleAgeFilters() {
        this.showAgeFilter = !this.showAgeFilter;
    }

    applyEmissionTypeFilter($event: any) {
        this.selectedEmissionTypes = MepLookupItem.getIdsFromLookups(this.emissionTypesHtml, $event.filters);
        this.lookupFiltersHandler.emissionTypes = this.emissionTypes.filter(x => $event.items.map(y => y.id).indexOf(x.id) > -1);

        this.portVesselsOdataGridViewModel.allowedEmissionTypesColumns = this.selectedEmissionTypes.length == 0 ?
            this.emissionTypes.map(x => x.name.toLowerCase()) : this.emissionTypes.filter(x => $event.filters.map(y => y.name).indexOf(x.displayName) > -1).map(x => x.name.toLowerCase());

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
        this.toggleEmissionTypeFilters();
    }

    applyPoiFilter($event: any) {
        this.selectedPointsOfInterest = MepLookupItem.getIdsFromLookups(this.pointsOfInterests, $event.filters);
        this.lookupFiltersHandler.pointsOfInterests = $event.items

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
        this.togglePoiFilters();
    }

    applyVesselTypeFilter($event: any) {
        this.selectedVesselTypeGroups = MepLookupItem.getIdsFromLookups(this.vesselTypeGroups, $event.filters);
        this.lookupFiltersHandler.vesselTypeGroups = $event.items

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
        this.toggleVesselTypeFilters();
    }

    applyOperationModeFilter($event: any) {
        this.selectedOperationModes = MepLookupItem.getIdsFromLookups(this.operationModes, $event.filters);
        this.lookupFiltersHandler.operationModes = $event.items

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
        this.toggleOperationModeFilters();
    }

    applyCargoTypeFilter($event: any) {
        this.selectedCargoTypes = MepLookupItem.getIdsFromLookups(this.cargoTypes, $event.filters);
        this.lookupFiltersHandler.cargoTypes = $event.items

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
        this.toggleCargoTypeFilters();
    }

    applySafetyRatingFilter(ratings: RatingItem[]) {
        this.selectedSafetyRatings = [];

        if (ratings && ratings.length > 0) {
            this.selectedSafetyRatings = ratings.map(x => x.rating);
        }

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
        this.toggleSafetyRatingFilters();
    }

    applyGhgRating(items: number[]) {
        this.selectedGhgRatingWeights = items;

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
        this.toggleGhgRatingFilters();
    }

    applyDwtFilter($event: any) {
        this.selectedDwtBucketIds = MepLookupItem.getIdsFromLookups(this.weightBuckets, $event.filters);
        this.lookupFiltersHandler.weightBuckets = $event.items

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
        this.toggleDwtFilters();
    }

    applyAgeFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.ageFirstFilter = filterValue.low;
            this.ageSecondFilter = filterValue.high;
            this.ageIsApplied = true;
        }

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
        this.toggleAgeFilters();
    }

    onSelectedMonthsChanged() {
        if (this.isLookupDataRefreshing)
            return;

        const refreshLookupsPromise = this.lookupFiltersHandler.onSelectedMonthsChanged();
        if (refreshLookupsPromise) {
            refreshLookupsPromise.then(() => {
                this.setEmissionTypesHtml();

                if (!this.disableEmissionTypesColumnsReload) {
                    this.portVesselsOdataGridViewModel.allowedEmissionTypesColumns = this.emissionTypes.map(x => x.name.toLowerCase());
                } else {
                    this.disableEmissionTypesColumnsReload = false;
                }

                // I need force refresh to update the columns for emission types
                this.applyFilters(true)
            })
        }
    }

    clearEmissionTypeFilter(shouldRefreshData: boolean = true) {
        this.selectedEmissionTypes = [];
        this.portVesselsOdataGridViewModel.allowedEmissionTypesColumns = (this.emissionTypes ?? []).map(x => x.name.toLowerCase());

        if (!shouldRefreshData)
            return;

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
    }

    clearPoiFilter(shouldRefreshData: boolean = true) {
        this.selectedPointsOfInterest = [];

        if (!shouldRefreshData)
            return;

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
    }

    clearVesselTypeFilter(shouldRefreshData: boolean = true) {
        this.selectedVesselTypeGroups = [];

        if (!shouldRefreshData)
            return;

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
    }

    clearOperationModeFilter(shouldRefreshData: boolean = true) {
        this.selectedOperationModes = [];

        if (!shouldRefreshData)
            return;

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
    }

    clearCargoTypeFilter(shouldRefreshData: boolean = true) {
        this.selectedCargoTypes = [];

        if (!shouldRefreshData)
            return;

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
    }

    clearSafetyRatingFilter(shouldRefreshData: boolean = true) {
        this.selectedSafetyRatings = [];
        this.mobileSafetyRatings = [];

        if (!shouldRefreshData)
            return;

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest())
    }

    clearGhgRatingFilter(shouldRefreshData: boolean = true) {
        this.selectedGhgRatingWeights = [];
        this.mobileGhgRatingWeights = [];

        if (!shouldRefreshData)
            return;

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest())
    }

    clearDwtFilter() {
        this.selectedDwtBucketIds = []
        this.mobileDwtFilter = [];

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest())
    }

    clearAgeFilter(shouldRefreshData: boolean = true) {
        this.ageFirstFilter = null;
        this.ageSecondFilter = null;
        this.mobileAgeFilter = { low: null, high: null };
        this.ageIsApplied = false;

        if (!shouldRefreshData)
            return;

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest())
    }

    clearAllFilters(shouldRefreshData: boolean = true) {
        this.clearEmissionTypeFilter(shouldRefreshData);
        this.clearPoiFilter(shouldRefreshData);
        this.clearOperationModeFilter(shouldRefreshData);
        this.clearCargoTypeFilter(shouldRefreshData);
        this.clearVesselTypeFilter(shouldRefreshData);
        this.clearSafetyRatingFilter(shouldRefreshData);
        this.clearGhgRatingFilter(shouldRefreshData);
        this.clearDwtFilter();
        this.clearAgeFilter(shouldRefreshData);

        if (!shouldRefreshData)
            return;

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest())
    }
 
    @computedFrom(Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedEmissionTypes),
    Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedEmissionTypes.length))
    get emissionTypeFilterActive(): boolean {
        return this.selectedEmissionTypes.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedPointsOfInterest),
    Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedPointsOfInterest.length))
    get poiFilterActive(): boolean {
        return this.selectedPointsOfInterest.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedVesselTypeGroups),
    Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedVesselTypeGroups.length))
    get vesselTypeFilterActive(): boolean {
        return this.selectedVesselTypeGroups.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedOperationModes),
    Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedOperationModes.length))
    get operationModeFilterActive(): boolean {
        return this.selectedOperationModes.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedCargoTypes),
    Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedCargoTypes.length))
    get cargoTypeFilterActive(): boolean {
        return this.selectedCargoTypes.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedSafetyRatings),
    Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedSafetyRatings.length))
    get safetyRatingFilterActive(): boolean {
        return this.selectedSafetyRatings.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedGhgRatingWeights),
    Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedGhgRatingWeights.length))
    get ghgRatingFilterActive(): boolean {
        return this.selectedGhgRatingWeights.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedDwtBucketIds),
    Reflection.getPropertyPath<PortVesselsFiltersViewModel>(z => z.selectedDwtBucketIds.length))
    get dwtFilterActive(): boolean {
        return this.selectedDwtBucketIds.length > 0;
    }

    getRequest(): GetVesselEmissionsRequest {

        return {
            portId: this.portId,
            emissionTypeIds: this.selectedEmissionTypes.join(),
            pointOfInterestIds: this.selectedPointsOfInterest.join(),
            vesselTypeIds: this.selectedVesselTypeGroups.join(),
            operationModeIds: this.selectedOperationModes.join(),
            scopeIds: this.selectedScopes.join(),
            cargoTypeIds: this.selectedCargoTypes.join(),
            safetyScores: this.selectedSafetyRatings.join(),
            ghgRatings: this.selectedGhgRatingWeights.join(),
            weightBucketIds: this.selectedDwtBucketIds.join(),
            ageFrom: this.ageFirstFilter ? this.ageFirstFilter : this.ageDefaults.low,
            ageTo: this.ageSecondFilter ? this.ageSecondFilter : this.ageDefaults.high,
            year: this.selectedYear ? this.selectedYear : null,
            months: this.selectedMonths ? this.selectedMonths.map(x => x.id).join() : '',
            searchFilter: this.searchFilter,
            portName: this.portName,
            dateRange: this.excelDateRangeLabel
        }
    }

    downloadExcel() {
        //If the current user is a customer with a trial account
        if (this.customerDetails?.isMepTrial) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.TrialVersionDialog, { customerId: this.customerDetails.customerId, title: 'Download XLS' });
        }
        else {
            if (this.isDownloading) return;

            this.isDownloading = true;

            const filtersRequest = this.getRequest();
            const emissionTypeIds = filtersRequest.emissionTypeIds.split(',').map(x => Number(x))

            const names = emissionTypeIds.every(x => x > 0) ? MepLookupItem.getSelectedItems(this.emissionTypes, emissionTypeIds).map(x => x.name.toLowerCase()) : this.emissionTypes.map(x => x.name.toLowerCase());
            const totalGHGEmissionsObj = { id: 0, name: 'totalGHGEmissions' } as MepLookupItem;
            if (names.indexOf('CO2'.toLowerCase()) > -1 && (names.indexOf('CH4'.toLowerCase()) > -1 || names.indexOf('N2O'.toLowerCase()) > -1)) {
                if (names.indexOf('totalGHGEmissions'.toLowerCase()) < 0) {
                    this.emissionTypes.push(totalGHGEmissionsObj);
                    emissionTypeIds.push(totalGHGEmissionsObj.id);
                }
            } else {
                if (names.indexOf('totalGHGEmissions'.toLowerCase()) > -1) {
                    this.emissionTypes.splice(this.emissionTypes.indexOf(totalGHGEmissionsObj), 1);
                    emissionTypeIds.splice(emissionTypeIds.indexOf(totalGHGEmissionsObj.id), 1);
                }
            }

            const request: GetVesselEmissionsRequest = {
                portId: filtersRequest.portId,
                emissionTypeIds: filtersRequest.emissionTypeIds,
                pointOfInterestIds: filtersRequest.pointOfInterestIds,
                vesselTypeIds: filtersRequest.vesselTypeIds,
                operationModeIds: filtersRequest.operationModeIds,
                cargoTypeIds: filtersRequest.cargoTypeIds,
                safetyScores: filtersRequest.safetyScores,
                ghgRatings: filtersRequest.ghgRatings,
                weightBucketIds: filtersRequest.weightBucketIds,
                ageFrom: filtersRequest.ageFrom,
                ageTo: filtersRequest.ageTo,
                year: filtersRequest.year,
                months: filtersRequest.months,
                scopeIds: filtersRequest.scopeIds,
                allowedEmissionTypes: emissionTypeIds.length >= 1 && emissionTypeIds[0] != 0 ? this.emissionTypes.filter(x => emissionTypeIds.indexOf(x.id) > -1).map(y => y.name.toUpperCase().replace('(', '').replace(')', '')).join()
                    : this.emissionTypes.map(x => x.name.toUpperCase().replace('(', '').replace(')', '')).join(),
                searchFilter: filtersRequest.searchFilter,
                portName: this.portName,
                dateRange: this.excelDateRangeLabel
            };

            this.mepService.downloadMepVessel(request)
                .then(() => {
                    this.isDownloading = false;
                    this.dialogClientService.show(RightShipDialogServiceConstants.URLs.GeneratingEmissionsDataDialog, null, true, false, false, 'center');
                }).catch(() => {
                    this.isDownloading = false;
                });
        }
    }

    downloadPortActivitiesExcelTemplate = () => {
        //If the current user is a customer with a trial account
        if (this.customerDetails?.isMepTrial) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.TrialVersionDialog, { customerId: this.customerDetails.customerId, title: 'Download XLS' });
        }
        else {
            if (this.isDownloadingTemplate) return;

            this.isDownloadingTemplate = true;

            const filtersRequest = this.getRequest();
            const emissionTypeIds = filtersRequest.emissionTypeIds.split(',').map(x => Number(x))

            const names = emissionTypeIds.every(x => x > 0) ? MepLookupItem.getSelectedItems(this.emissionTypes, emissionTypeIds).map(x => x.name.toLowerCase()) : this.emissionTypes.map(x => x.name.toLowerCase());
            const totalGHGEmissionsObj = { id: 0, name: 'totalGHGEmissions' } as MepLookupItem;
            if (names.indexOf('CO2'.toLowerCase()) > -1 && (names.indexOf('CH4'.toLowerCase()) > -1 || names.indexOf('N2O'.toLowerCase()) > -1)) {
                if (names.indexOf('totalGHGEmissions'.toLowerCase()) < 0) {
                    this.emissionTypes.push(totalGHGEmissionsObj);
                    emissionTypeIds.push(totalGHGEmissionsObj.id);
                }
            } else {
                if (names.indexOf('totalGHGEmissions'.toLowerCase()) > -1) {
                    this.emissionTypes.splice(this.emissionTypes.indexOf(totalGHGEmissionsObj), 1);
                    emissionTypeIds.splice(emissionTypeIds.indexOf(totalGHGEmissionsObj.id), 1);
                }
            }

            const request: GetVesselEmissionsRequest = {
                portId: filtersRequest.portId,
                emissionTypeIds: filtersRequest.emissionTypeIds,
                pointOfInterestIds: filtersRequest.pointOfInterestIds,
                vesselTypeIds: filtersRequest.vesselTypeIds,
                operationModeIds: filtersRequest.operationModeIds,
                cargoTypeIds: filtersRequest.cargoTypeIds,
                safetyScores: filtersRequest.safetyScores,
                ghgRatings: filtersRequest.ghgRatings,
                weightBucketIds: filtersRequest.weightBucketIds,
                ageFrom: filtersRequest.ageFrom,
                ageTo: filtersRequest.ageTo,
                year: filtersRequest.year,
                months: filtersRequest.months,
                scopeIds: filtersRequest.scopeIds,
                allowedEmissionTypes: emissionTypeIds.length >= 1 && emissionTypeIds[0] != 0 ? this.emissionTypes.filter(x => emissionTypeIds.indexOf(x.id) > -1).map(y => y.name.toUpperCase().replace('(', '').replace(')', '')).join()
                    : this.emissionTypes.map(x => x.name.toUpperCase().replace('(', '').replace(')', '')).join(),
                searchFilter: filtersRequest.searchFilter,
                portName: this.portName,
                dateRange: this.excelDateRangeLabel
            };

            this.mepService.downloadMepVesselPortActivitiesTemplate(request, this.portName)
                .then(() => {
                    this.isDownloadingTemplate = false;
                }).catch(() => {
                    this.isDownloadingTemplate = false;
                });
        }
    }

    setEmissionTypesHtml() {
        this.emissionTypesHtml = JSON.parse(JSON.stringify(this.emissionTypes));
        this.emissionTypesHtml.forEach(x => {
            x.name = x.displayName
        });
    }

    //#region Scope filter

    public showScopeFilter: boolean = false;
    public selectedScopes: number[] = [];

    get scopeFilterActive(): boolean {
        return this.selectedScopes.length > 0;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.scopes')
    get scopes(): MepLookupItem[] {
        return this.lookupFiltersHandler.scopes;
    }

    toggleScopeFilters() {
        this.showScopeFilter = !this.showScopeFilter;
    }

    applyScopeFilter($event: any) {
        this.selectedScopes = MepLookupItem.getIdsFromLookups(this.scopes, $event.filters);
        this.lookupFiltersHandler.scopes = $event.items

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
        this.toggleScopeFilters();
    }

    clearScopeFilter(shouldRefreshData: boolean = true) {
        this.selectedScopes = [];

        if (!shouldRefreshData)
            return;

        this.portVesselsOdataGridViewModel.refreshItemSource(this.getRequest());
    }

    //#endregion
}
